import {
  faArrowUpRightFromSquare,
  faCheck,
  faClock,
  faEllipsisV,
  faPause,
  faPencil,
  faPlay,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Menu } from '@headlessui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { memo, useContext } from 'react';
import type { JSX } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import type { Props } from '@/components/TaskMenu/types';
import AppContext from '@/contexts/appContext';
import WorkRepository from '@/repositories/WorkRepository';
import { Item, ItemPayload } from '@/types/item';
import { Tooltip } from '@/ui/Tooltip';
import { kustomerConversationLink } from '@/utils/kustomerLinks';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';
import { hasPermission, ITEM_DELETE } from '@/utils/userPermission';

const TaskMenu = ({ item }: Props): JSX.Element => {
  const { addMessage, host, permissions } = useContext(AppContext);
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const workRepo = WorkRepository();

  const invalidateTaskQueries = () => {
    return Promise.all([
      queryClient.invalidateQueries({ queryKey: ['getPersonTasks', item.person_id] }),
      queryClient.invalidateQueries({ queryKey: ['getTasks', item.taskable_id] }),
      queryClient.invalidateQueries({ queryKey: ['getAllTasks'] }),
      queryClient.invalidateQueries({ queryKey: ['getItem', item.id] })
    ]);
  };

  const updateItem = useMutation({
    mutationFn: async ({ id, values }: { id: string; values: Partial<ItemPayload> }) => {
      return workRepo.updateItem(id, values);
    },
    onSuccess: () => {
      void invalidateTaskQueries();
      addMessage({ level: 'success', text: 'Successfully updated task' });
    },
    onError: (error: AxiosError) => addMessage(mapAxiosErrorMessages(error))
  });

  const completeItem = (item: Item) => {
    if (item.id) {
      updateItem.mutate({
        id: item.id,
        values: {
          status: 'closed'
        }
      });
    }
  };

  const startItem = (item: Item) => {
    if (item.id) {
      updateItem.mutate({
        id: item.id,
        values: {
          status: 'in_progress'
        }
      });
    }
  };

  const unclaimItem = (item: Item) => {
    if (item.id) {
      updateItem.mutate({
        id: item.id,
        values: {
          status: 'open',
          assigned_user_id: null
        }
      });
    }
  };

  const deleteItem = useMutation({
    mutationFn: (item: Item) => {
      if (item.id) {
        return workRepo.deleteItem(item.id);
      } else {
        return Promise.reject('Item does not have an ID');
      }
    },
    onSuccess: () => {
      void invalidateTaskQueries();
      addMessage({ level: 'success', text: 'Successfully deleted task' });
      const taskListRoute = item.person_id ? `/people/${item.person_id}/tasks` : '/tasks';
      if (location.pathname !== taskListRoute) {
        navigate(taskListRoute);
      }
    },
    onError: (error: AxiosError) => addMessage(mapAxiosErrorMessages(error))
  });

  const canDelete = hasPermission({ permissions, requiredPermission: ITEM_DELETE });

  const confirmDeleteItem = (item: Item) => {
    if (!window.confirm(`Delete "${item.name}"? This can be restored by an engineer.`)) return;
    deleteItem.mutate(item);
  };

  const isItemClosed = item.status === 'closed';
  const isItemInProgress = item.status === 'in_progress';

  return (
    <div className="flex items-center gap-x-4">
      {/* Mark In Progress / Unclaim */}
      <Tooltip
        content={isItemInProgress ? 'Unclaim and release the work' : 'Claim and mark in progress'}
        placement="top">
        <button
          disabled={isItemClosed}
          onClick={() => (isItemInProgress ? unclaimItem(item) : startItem(item))}
          className="text-gray-400 hover:text-gray-500 disabled:cursor-not-allowed disabled:opacity-50"
          title={isItemInProgress ? 'Unclaim' : 'Mark In Progress'}>
          <span className="sr-only">{isItemInProgress ? 'Unclaim' : 'Mark In Progress'}</span>
          <FontAwesomeIcon
            icon={isItemInProgress ? faPause : faPlay}
            className={isItemInProgress ? 'text-blue-600' : ''}
          />
        </button>
      </Tooltip>

      {/* Mark Complete */}
      <Tooltip content="Claim and mark complete" placement="top">
        <button
          disabled={isItemClosed}
          onClick={() => completeItem(item)}
          className="text-gray-400 hover:text-gray-500 disabled:cursor-not-allowed disabled:opacity-50"
          title="Mark Complete">
          <span className="sr-only">Mark Complete</span>
          <FontAwesomeIcon icon={faCheck} className={isItemClosed ? 'text-green-500' : ''} />
        </button>
      </Tooltip>

      {/* Snooze */}
      {/* TODO: Add modal to allow setting a snooze */}
      <Tooltip content="Snooze — hide until a later date" placement="top">
        <button className="text-gray-400 hover:text-gray-500" title="Snooze">
          <span className="sr-only">Snooze</span>
          <FontAwesomeIcon icon={faClock} />
        </button>
      </Tooltip>

      <Menu as="div" className="relative flex-none">
        <Menu.Button
          className="-m-2.5 block p-2.5 text-gray-500 hover:text-gray-900"
          title="More actions">
          <span className="sr-only">Open options</span>
          <FontAwesomeIcon icon={faEllipsisV} />
        </Menu.Button>

        <Menu.Items className="absolute right-0 z-10 mt-2 w-40 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-hidden">
          <Menu.Item>
            {({ active }) => (
              <a
                aria-label="View in Homebase"
                href="#"
                className={`${
                  active ? 'bg-gray-50' : ''
                } block px-3 py-1 text-sm leading-6 text-gray-900`}>
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} /> View in Homebase
                <span className="sr-only">{item.name}</span>
              </a>
            )}
          </Menu.Item>

          {item.conversation_kustomer_id && (
            <Menu.Item>
              {({ active }) => (
                <a
                  aria-label="View in Kustomer"
                  href={kustomerConversationLink({
                    host,
                    conversationKustomerId: item.conversation_kustomer_id as string
                  })}
                  rel="noreferrer"
                  target="_blank"
                  className={`${
                    active ? 'bg-gray-50' : ''
                  } block px-3 py-1 text-sm leading-6 text-gray-900`}>
                  <FontAwesomeIcon icon={faArrowUpRightFromSquare} /> View in Kustomer
                  <span className="sr-only">, {item.name}</span>
                </a>
              )}
            </Menu.Item>
          )}

          <Menu.Item>
            {({ active }) => (
              <Link
                aria-label="Edit"
                to={
                  item.person_id
                    ? `/people/${item.person_id}/tasks/${item.id}/edit`
                    : `/tasks/${item.id}/edit`
                }
                className={`${
                  active ? 'bg-gray-50' : ''
                } block px-3 py-1 text-sm leading-6 text-gray-900`}>
                <FontAwesomeIcon icon={faPencil} /> Edit
                <span className="sr-only">, {item.name}</span>
              </Link>
            )}
          </Menu.Item>

          {canDelete && (
            <Menu.Item>
              {({ active }) => (
                <button
                  aria-label="Delete"
                  className={`${
                    active ? 'bg-gray-50' : ''
                  } block w-full px-3 py-1 text-left text-sm leading-6 text-gray-900`}
                  type="button"
                  onClick={() => confirmDeleteItem(item)}>
                  <FontAwesomeIcon icon={faTrash} className="text-red-500" /> Delete
                  <span className="sr-only">, {item.name}</span>
                </button>
              )}
            </Menu.Item>
          )}
        </Menu.Items>
      </Menu>
    </div>
  );
};

export default memo(TaskMenu);
